/**
 * Meeting Points Download Section Component
 * 
 * Section for downloading and importing meeting points (hubs) from UAT/Production environments.
 * Follows the same pattern as GeofencesDownloadSection but adapted for hubs/meeting points.
 */

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  AlertTitle,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  InputAdornment,
  IconButton,
  LinearProgress,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';

import { fetchHubs, fetchNetworkSettings, type UATNetwork, type UATNetworkSettingsResponse } from '../../../services/fixedRouteUatClient';
import {
  importUATMeetingPoints,
  importUATPointsOfInterest,
  type HubImportResult,
} from '../../../services/fixedRouteUatImporter';
import { AppContext } from '../../../context/context';
import { type Environment, getEnvironmentDisplayName } from './EnvironmentSelector';
import type { UATHub } from '../../../services/fixedRouteUatClient';
import { getCachedHubsList, setCachedHubsList } from './hubsCache';

import { Icon } from '@liftango/ui-icons';
interface MeetingPointsDownloadSectionProps {
  jwtToken: string;
  environment: Environment;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  serviceDate: string;
  onServiceDateChange: (date: string) => void;
  timezone: string;
  onTimezoneChange: (tz: string) => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

/**
 * Helper function to get a stable ID from a hub object
 * Uses addressId from the API response, or generates a stable fallback
 */
function getHubId(hub: UATHub): string {
  // Use addressId from API response (stable identifier)
  if (hub.addressId) return hub.addressId;
  // Fallback: use name if available
  if (hub.name) return `hub-${hub.name}`;
  // Last resort: generate based on coordinates (stable)
  if (typeof hub.latitude === 'number' && typeof hub.longitude === 'number') {
    return `hub-${hub.latitude}-${hub.longitude}`;
  }
  // Final fallback (should rarely happen)
  return `hub-unknown-${Date.now()}-${Math.random()}`;
}

export const MeetingPointsDownloadSection: React.FC<MeetingPointsDownloadSectionProps> = ({
  jwtToken,
  environment,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  serviceDate,
  onServiceDateChange,
  timezone,
  onTimezoneChange,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapDownload');
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('MeetingPointsDownloadSection must be used within AppContext');
  }

  // Date range state (required for hubs API)
  // Initialize dates first so we can use them in state initializers below
  const initialStartDate = serviceDate || '';
  const initialEndDate = (() => {
    if (serviceDate) {
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      return date.toISOString().split('T')[0];
    }
    return '';
  })();
  
  const [startDate, setStartDate] = useState<string>(initialStartDate);
  const [endDate, setEndDate] = useState<string>(initialEndDate);

  // Network settings for timezone-agnostic pattern
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState<boolean>(false);

  // Calculate effective timezone from network settings
  const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;

  const [meetingPointSearchQuery, setMeetingPointSearchQuery] = useState<string>('');

  // Download state - Initialize from cache immediately to prevent flash
  const [hubsList, setHubsList] = useState<UATHub[]>(() => {
    // Check cache synchronously during initialization
    if (jwtToken.trim() && selectedProductId && initialStartDate && initialEndDate) {
      const cached = getCachedHubsList(environment, selectedProductId, initialStartDate, initialEndDate);
      if (cached && cached.length > 0) {
        // Filter to exclude POIs (meeting points are hubs that are NOT type "poi")
        return cached.filter((hub: UATHub) => hub.type !== 'poi');
      }
    }
    return [];
  });
  const [selectedHubIds, setSelectedHubIds] = useState<Set<string>>(() => {
    // Initialize selected IDs from cached data
    if (jwtToken.trim() && selectedProductId && initialStartDate && initialEndDate) {
      const cached = getCachedHubsList(environment, selectedProductId, initialStartDate, initialEndDate);
      if (cached && cached.length > 0) {
        const meetingPoints = cached.filter((hub: UATHub) => hub.type !== 'poi');
        if (meetingPoints.length > 0) {
          return new Set(meetingPoints.map((hub: UATHub) => getHubId(hub)));
        }
      }
    }
    return new Set();
  });
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [expandedHubIds, setExpandedHubIds] = useState<Set<string>>(new Set());

  // Import state
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importStep, setImportStep] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<HubImportResult[] | null>(null);

  // Load network settings when product is selected (timezone-agnostic pattern)
  useEffect(() => {
    if (selectedProductId && jwtToken.trim() && !isLoadingNetworkSettings) {
      handleLoadNetworkSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, jwtToken]);

  // Update parent timezone when network settings load
  useEffect(() => {
    if (networkSettings?.data?.networks?.[0]?.networkSettings?.timezone) {
      const networkTimezone = networkSettings.data.networks[0].networkSettings.timezone;
      if (networkTimezone !== timezone) {
        onTimezoneChange(networkTimezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkSettings]);

  // Update startDate when serviceDate changes
  useEffect(() => {
    if (serviceDate && serviceDate !== startDate) {
      setStartDate(serviceDate);
      // Update end date to 30 days after start date
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      setEndDate(date.toISOString().split('T')[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceDate]);

  // Track previous jwtToken and environment to only reset when they actually change
  const prevJwtTokenRef = useRef<string>(jwtToken);
  const prevEnvironmentRef = useRef<Environment>(environment);

  // Reset state when environment or JWT token changes (but not on initial mount)
  useEffect(() => {
    const jwtTokenChanged = prevJwtTokenRef.current !== jwtToken;
    const environmentChanged = prevEnvironmentRef.current !== environment;

    if (jwtTokenChanged || environmentChanged) {
      // Only reset if values actually changed (not on initial mount)
      setHubsList([]);
      setSelectedHubIds(new Set());
      setDownloadError(null);
      setImportResults(null);
      setImportError(null);
      
      // Update refs
      prevJwtTokenRef.current = jwtToken;
      prevEnvironmentRef.current = environment;
    } else {
      // On initial mount, just update refs without resetting
      prevJwtTokenRef.current = jwtToken;
      prevEnvironmentRef.current = environment;
    }
  }, [jwtToken, environment]);

  // Auto-download when component mounts or key parameters change (only if no data loaded)
  useEffect(() => {
    if (
      jwtToken.trim() &&
      selectedProductId &&
      startDate &&
      endDate &&
      !isDownloading &&
      hubsList.length === 0
    ) {
      // Only trigger download if we don't have data (cache was already checked in state initialization)
      const timer = setTimeout(() => {
        handleDownload();
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductId, startDate, endDate, jwtToken, environment]);

  const handleLoadNetworkSettings = async () => {
    if (!jwtToken.trim() || !selectedProductId) return;

    setIsLoadingNetworkSettings(true);
    try {
      const settings = await fetchNetworkSettings(jwtToken, selectedProductId, environment);
      setNetworkSettings(settings);
    } catch (error) {
      console.warn('[MeetingPointsDownloadSection] Failed to load network settings:', error);
      // Don't set error - this is optional for timezone
    } finally {
      setIsLoadingNetworkSettings(false);
    }
  };

  const handleDownload = async () => {
    if (!jwtToken.trim()) { setDownloadError(t('common.errors.jwtRequired')); return; }
    if (!selectedProductId) { setDownloadError(t('common.errors.productRequired')); return; }
    if (!startDate) { setDownloadError(t('common.errors.startDateRequired')); return; }
    if (!endDate) { setDownloadError(t('common.errors.endDateRequired')); return; }
    if (new Date(endDate) < new Date(startDate)) {
      setDownloadError(t('common.errors.endBeforeStart')); return;
    }

    // Check cache first before clearing state
    const cachedHubs = getCachedHubsList(environment, selectedProductId, startDate, endDate);
    if (cachedHubs && cachedHubs.length > 0) {
      console.log('[MeetingPointsDownloadSection] Using cached hubs:', cachedHubs.length);
      // Filter to exclude POIs (meeting points are hubs that are NOT type "poi")
      const meetingPoints = cachedHubs.filter((hub: UATHub) => hub.type !== 'poi');
      console.log('[MeetingPointsDownloadSection] Filtered to meeting points:', meetingPoints.length);
      setHubsList(meetingPoints);
      // Select all meeting points by default
      const hubIds = meetingPoints.map((hub: UATHub) => getHubId(hub));
      setSelectedHubIds(new Set(hubIds));
      setDownloadError(null);
      return;
    }

    // No cache - proceed with API call
    setIsDownloading(true);
    setDownloadError(null);
    setHubsList([]);
    setSelectedHubIds(new Set());

    try {

      console.log('[MeetingPointsDownloadSection] Fetching hubs...', {
        productId: selectedProductId,
        startDate,
        endDate,
      });
      
      const allHubs = await fetchHubs(
        jwtToken,
        selectedProductId,
        startDate,
        endDate,
        environment
      );

      // Filter to exclude POIs (meeting points are hubs that are NOT type "poi")
      const hubs = allHubs.filter((hub: UATHub) => hub.type !== 'poi');

      console.log('[MeetingPointsDownloadSection] Fetched and filtered meeting points:', {
        totalHubs: allHubs.length,
        meetingPointsCount: hubs.length,
        hubs: hubs,
      });

      // Cache the full response (all hubs) so POIs can also use it
      setCachedHubsList(environment, selectedProductId, startDate, endDate, allHubs);

      setHubsList(hubs);

      // Select all hubs by default
      const hubIds = hubs.map((hub: UATHub) => getHubId(hub));
      console.log('[MeetingPointsDownloadSection] Generated hub IDs:', hubIds);
      setSelectedHubIds(new Set(hubIds));

      // Clear any previous errors
      setDownloadError(null);

    } catch (error) {
      let errorMessage = t('meetingPoints.errors.downloadFailedBase', { environment: getEnvironmentDisplayName(environment) });
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = t('common.errors.authFailed');
        } else if (error.message.includes('404')) {
          errorMessage = t('meetingPoints.errors.notFound');
        } else if (error.message.includes('429')) {
          errorMessage = t('common.errors.tooManyRequests');
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = t('common.errors.serverError', { environment: getEnvironmentDisplayName(environment) });
        }
      }
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImport = async () => {
    // Auto-select all if nothing is selected
    let hubsToImport = Array.from(selectedHubIds).map(id => hubsList.find(h => getHubId(h) === id)).filter(Boolean) as typeof hubsList;
    if (hubsToImport.length === 0) {
      hubsToImport = hubsList; // If nothing selected, import all
      setSelectedHubIds(new Set(hubsList.map(getHubId))); // Update UI selection
    }

    if (!context) {
      setImportError(t('common.errors.contextNotAvailable'));
      return;
    }

    setIsImporting(true);
    setImportError(null);
    setImportProgress(0);
    setImportStep(t('meetingPoints.stepInitializing'));

    try {
      // Use hubsToImport (either selected or all)
      setImportStep(t('meetingPoints.stepImporting', { count: hubsToImport.length }));
      setImportProgress(50);

      // Import meeting points
      const results = importUATMeetingPoints(hubsToImport, context);

      setImportProgress(100);
      setImportStep(t('meetingPoints.stepComplete'));

      setImportResults(results);

      // Clear selection after successful import
      setSelectedHubIds(new Set());

    } catch (error) {
      console.error('[MeetingPointsDownloadSection] Import error:', error);
      setImportError(error instanceof Error ? error.message : t('meetingPoints.errors.importGeneric'));
    } finally {
      setIsImporting(false);
    }
  };

  const selectedProduct = networks.find(p => p.productId === selectedProductId);
  const totalSelectedHubs = selectedHubIds.size;
  // Import button is always available if meeting points are present (will auto-select all if nothing selected)
  const canImport = hubsList.length > 0 && !isImporting;
  const canDownload = jwtToken.trim() && selectedProductId && startDate && endDate && !isDownloading;

  const filteredHubs = hubsList.filter((hub) => {
    if (!meetingPointSearchQuery) return true;
    const searchLower = meetingPointSearchQuery.toLowerCase();
    const hubId = hub.addressId || '';
    const hubName = hub.name || '';
    const hubLabel = hub.label || '';
    const hubType = hub.type || '';
    return (
      hubName.toLowerCase().includes(searchLower) ||
      hubLabel.toLowerCase().includes(searchLower) ||
      hubId.toLowerCase().includes(searchLower) ||
      hubType.toLowerCase().includes(searchLower)
    );
  });

  if (!downloadAcknowledged) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
        p: 2,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <img
          src="/icons/consent-img.png"
          alt={t('common.consentImageAlt')}
          style={{ 
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'center center'
          }} 
        />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left Column - Summary Section (when meeting points are present) */}
        {hubsList.length > 0 && (
          <Box
            sx={{
              width: '45%',
              minWidth: 280,
              display: 'flex',
              flexDirection: 'column',
              position: 'sticky',
              top: 0,
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              maxHeight: 'calc(100vh - 200px)',
            }}
          >
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('meetingPoints.summaryAboutToImport', { count: totalSelectedHubs > 0 ? totalSelectedHubs : hubsList.length })}
            </Typography>
            {selectedProduct && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('meetingPoints.network', { name: selectedProduct.productName })}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              {t('meetingPoints.dateRange', { start: startDate, end: endDate })}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Import Button - Always visible above the scrollable list */}
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleImport}
              disabled={!canImport}
              fullWidth
              startIcon={isImporting ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
              sx={{ mb: 2 }}
            >
              {isImporting
                ? t('common.importing')
                : totalSelectedHubs > 0
                  ? t('meetingPoints.importButton', { count: totalSelectedHubs })
                  : t('meetingPoints.importAllButton', { count: hubsList.length })
              }
            </Button>

            {isImporting && (
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={importProgress} />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {importStep || t('meetingPoints.importingMeetingPoints')} ({Math.round(importProgress)}%)
                </Typography>
              </Box>
            )}

            {importError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>{t('common.importFailed')}</AlertTitle>
                {importError}
              </Alert>
            )}

            {importResults && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>{t('common.importComplete')}</AlertTitle>
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  <strong>{t('meetingPoints.results.label')}</strong>{' '}
                  {t('meetingPoints.results.summary', {
                    created: importResults.filter(r => r.action === 'created').length,
                    skipped: importResults.filter(r => r.action === 'skipped').length,
                  })}
                </Typography>
                {importResults.filter(r => r.action === 'skipped').length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
                      {t('meetingPoints.results.skippedTitle', { count: importResults.filter(r => r.action === 'skipped').length })}
                    </Typography>
                    {importResults
                      .filter(r => r.action === 'skipped')
                      .slice(0, 5)
                      .map((result) => (
                        <Typography key={result.hubId} variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                          {t('meetingPoints.results.skippedItem', { id: result.hubId, reason: result.reason || t('common.unknownReason') })}
                        </Typography>
                      ))}
                    {importResults.filter(r => r.action === 'skipped').length > 5 && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                        {t('common.andMoreCount', { max: importResults.filter(r => r.action === 'skipped').length - 5 })}
                      </Typography>
                    )}
                  </Box>
                )}
              </Alert>
            )}
            
            <List 
              dense 
              disablePadding
              sx={{
                flex: 1,
                overflowY: 'auto',
              }}
            >
              {hubsList
                .filter(hub => {
                  const hubId = getHubId(hub);
                  return selectedHubIds.has(hubId);
                })
                .map((hub) => {
                  const hubId = getHubId(hub);
                  const isExpanded = expandedHubIds.has(hubId);
                  return (
                    <Accordion
                      key={hubId}
                      expanded={isExpanded}
                      onChange={() => {
                        const newExpanded = new Set(expandedHubIds);
                        if (isExpanded) {
                          newExpanded.delete(hubId);
                        } else {
                          newExpanded.add(hubId);
                        }
                        setExpandedHubIds(newExpanded);
                      }}
                      sx={{
                        '&:before': { display: 'none' },
                        boxShadow: 'none',
                        border: 'none',
                        '&.Mui-expanded': { margin: 0 },
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<Icon size={20} name="chevron-down" />}
                        sx={{
                          px: 0,
                          py: 1,
                          minHeight: 'auto',
                          '&.Mui-expanded': { minHeight: 'auto' },
                          '& .MuiAccordionSummary-content': {
                            my: 0,
                            '&.Mui-expanded': { my: 0 },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              bgcolor: '#4caf50',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={20} name="location-dot" style={{ color: 'white'}} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {hub.name || hubId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hub.label || t('meetingPoints.noAddress')}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pb: 1.5, pt: 0, pl: 5.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('meetingPoints.detail.addressId', { id: hub.addressId })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('meetingPoints.detail.coordinates', { lat: hub.latitude.toFixed(6), lng: hub.longitude.toFixed(6) })}
                          </Typography>
                          {hub.placeId && (
                            <Typography variant="caption" color="text.secondary">
                              {t('meetingPoints.detail.placeId', { id: hub.placeId.substring(0, 30) })}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {t('meetingPoints.detail.snapRadius', { radius: hub.snapRadius })}
                          </Typography>
                          {hub.type && (
                            <Typography variant="caption" color="text.secondary">
                              {t('meetingPoints.detail.type', { type: hub.type })}
                            </Typography>
                          )}
                          {hub.icon && (
                            <Typography variant="caption" color="text.secondary">
                              {t('meetingPoints.detail.icon', { icon: hub.icon })}
                            </Typography>
                          )}
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </List>
          </Box>
        )}

        {/* Right Column - Controls */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack spacing={2}>
            {networkError && (
              <Alert severity="error">
                {networkError}
              </Alert>
            )}

            {/* Show loading state during initial auto-download */}
            {isDownloading && hubsList.length === 0 && !downloadError && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {t('meetingPoints.loadingMeetingPoints')}
                </Typography>
              </Box>
            )}

            {/* Date Range Selection - Hide during initial auto-download */}
            {!(isDownloading && hubsList.length === 0) && (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={t('meetingPoints.startDateLabel')}
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    if (e.target.value) {
                      onServiceDateChange(e.target.value);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  disabled={isDownloading || isImporting}
                />
                <TextField
                  label={t('meetingPoints.endDateLabel')}
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  disabled={isDownloading || isImporting}
                  error={!!(endDate && startDate && new Date(endDate) < new Date(startDate))}
                  helperText={endDate && startDate && new Date(endDate) < new Date(startDate) ? t('common.errors.endBeforeStart') : ''}
                />
              </Box>
            )}

            {/* Download Button - Hide during initial auto-download */}
            {!(isDownloading && hubsList.length === 0) && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleDownload}
                disabled={!canDownload}
                fullWidth
                startIcon={isDownloading ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
              >
                {isDownloading ? t('common.downloading') : t('meetingPoints.downloadButton')}
              </Button>
            )}

            {/* Show loading indicator only when downloading with existing data (manual download) */}
            {isDownloading && hubsList.length > 0 && (
              <Box>
                <LinearProgress />
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {t('common.loadingPreview')}
                </Typography>
              </Box>
            )}

            {downloadError && (
              <Alert severity="error">
                <AlertTitle>{t('common.downloadFailed')}</AlertTitle>
                {downloadError}
              </Alert>
            )}

            {/* Preview Section */}
            {hubsList.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Button
                      size="small"
                      onClick={() => {
                        if (selectedHubIds.size === hubsList.length) {
                          setSelectedHubIds(new Set());
                        } else {
                          setSelectedHubIds(new Set(hubsList.map((hub: UATHub) => getHubId(hub))));
                        }
                      }}
                      disabled={isImporting}
                      sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                    >
                      {selectedHubIds.size === hubsList.length ? t('common.deselectAll') : t('common.selectAll')}
                    </Button>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('meetingPoints.previewLabel', { count: hubsList.length })}
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t('meetingPoints.searchPlaceholder')}
                    value={meetingPointSearchQuery}
                    onChange={(e) => setMeetingPointSearchQuery(e.target.value)}
                    disabled={isImporting}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Icon name="magnifying-glass" size={20} />
                        </InputAdornment>
                      ),
                      endAdornment: meetingPointSearchQuery && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setMeetingPointSearchQuery('')}
                            edge="end"
                          >
                            <Icon name="xmark" size={20} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 1 }}
                  />

                  <List 
                    dense 
                    sx={{ 
                      maxHeight: '400px',
                      overflow: 'auto',
                      mb: 2,
                    }}
                  >
                    {filteredHubs.length === 0 ? (
                      <ListItem>
                        <ListItemText
                          primary={t('meetingPoints.noMeetingPointsFound')}
                          secondary={meetingPointSearchQuery ? t('common.tryAdjustingSearch') : t('meetingPoints.noMeetingPointsAvailable')}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ) : (
                      filteredHubs.map((hub) => {
                        const hubId = getHubId(hub);
                        const isSelected = selectedHubIds.has(hubId);
                        return (
                          <ListItem key={hubId} sx={{ py: 0.5 }}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    const newSet = new Set(selectedHubIds);
                                    if (e.target.checked) {
                                      newSet.add(hubId);
                                    } else {
                                      newSet.delete(hubId);
                                    }
                                    setSelectedHubIds(newSet);
                                  }}
                                  disabled={isImporting}
                                />
                              }
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                  <Box
                                    sx={{
                                      width: 40,
                                      height: 40,
                                      borderRadius: '50%',
                                      bgcolor: '#4caf50',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <Icon size={20} name="location-dot" style={{ color: 'white'}} />
                                  </Box>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                      {hub.name || hubId}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {hub.label || t('meetingPoints.noAddress')}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                            />
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Box>
              </>
            )}
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default MeetingPointsDownloadSection;

